// 本人確認の state。`images` には本人確認済みの枠だけが入る（済みでない枠は持たない）。
// 進行は `submitting`（送信中の requestId）、失敗は画面に 1 つの `failure` で持つ
import { http } from '@/apis/aipass'
import { RootState } from '@/app/store'
import { env } from '@/libs/env'
import { PreparedImage, prepareImageForUpload } from '@/libs/heic'
import { toSafeFileName } from '@/libs/image-file-name'
import { PayloadAction, createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import axios, { AxiosError } from 'axios'
import uuid from 'react-uuid'

/**
 * 日本在住の身分証は種類（免許証 / マイナンバーカード / 在留カード）に関わらず
 * `drivingLicenseFront` / `drivingLicenseBack` を渡す（判定ラベルが汎用のため）。
 */
export type VerificationApiIdType = 'drivingLicenseFront' | 'drivingLicenseBack' | 'passport'

export type IdentificationUploadKey = {
  policy: string
  signature: string
  credential: string
  signedDate: string
  s3url: string
  allowPath: string
}

const IdentificationUploadFeature = 'checkin-image'

export type UploadedIdentificationImage = {
  slotId: string
  idType: VerificationApiIdType
  objectKey: string
  previewUrl: string
  nationality?: string
  staffRegisterLabel: string
  order: number
}

const appendUuidToFilename = (filename: string): string => {
  const suffix = uuid()
  const dotIndex = filename.lastIndexOf('.')
  if (dotIndex === -1) {
    return `${filename}_${suffix}`
  }
  return `${filename.slice(0, dotIndex)}_${suffix}${filename.slice(dotIndex)}`
}

/**
 * 署名の発行可否は予約 UUID で判定される（チェックアウト済みの予約は拒否される）。
 * 連泊で前半がチェックアウト済みの予約も選択中に含まれ得るため、チェックアウト日が今日以降のものを渡す。
 * 該当が無ければ予約なしとして送る。
 */
export const pickUploadReservationUuid = (
  reservations: ReadonlyArray<{ id: string; checkoutDate: string }> | undefined,
  today: string,
): string | undefined => reservations?.find(reservation => reservation.checkoutDate >= today)?.id

/**
 * アップロード先の署名を取る。
 * **有効期限は15分なので、アップロードのたびに取り直す。** 使い回してはいけない。
 */
export const fetchIdentificationUploadKey = async (hotelId: string, reservationUuid?: string): Promise<IdentificationUploadKey> => {
  const response = await http<IdentificationUploadKey>({
    method: 'get',
    url: `${env('BASE_API_V3')}/auth/s3-upload-key`,
    params: { hotelId, feature: IdentificationUploadFeature, isPublic: false, reservationUuid },
  })
  return response.data
}

/**
 * 署名を取り直してから S3 へ直接アップロードする。
 *
 * 画像は API 本体を通さない（サーバの転送量とメモリを避ける既存の設計）。
 * 呼び出し側が `src/libs/downscale.ts` の `MaxUploadBytes`（判定が使う Rekognition の上限 5MB）まで
 * 縮小してから渡すこと。
 */
export const uploadIdentificationImage = async (arg: {
  hotelId: string
  reservationUuid?: string
  file: Blob
  fileName: string
}): Promise<{ objectKey: string }> => {
  const uploadKey = await fetchIdentificationUploadKey(arg.hotelId, arg.reservationUuid)

  const objectKey = `${uploadKey.allowPath}/${appendUuidToFilename(toSafeFileName(arg.fileName))}`
  const contentType = arg.file.type || 'image/jpeg'

  const form = new FormData()
  form.append('key', objectKey)
  form.append('policy', uploadKey.policy)
  form.append('X-Amz-Credential', uploadKey.credential)
  form.append('X-Amz-Algorithm', 'AWS4-HMAC-SHA256')
  form.append('X-Amz-Date', uploadKey.signedDate)
  form.append('X-Amz-Signature', uploadKey.signature)
  form.append('Content-Type', contentType)
  form.append('file', arg.file)

  // S3 は別オリジンなので `http()` を通さず axios を直接使う（認証ヘッダも不要）
  await axios.post(uploadKey.s3url, form)

  return { objectKey }
}

export type VerifyIdentificationSuccess = {
  verify: boolean
  nationality?: string
}

/**
 * 判定APIの失敗は2種類ある。取り違えると撮り直しに繋がらない。
 * - `unreadable`：`400`「ファイルを読み取れませんでした」→ アップロードのやり直し
 * - `requestFailed`：通信そのものの失敗 → 再試行
 *
 * `200` + `verify: false`（撮り直し）は失敗ではなく正常応答（`VerifyIdentificationSuccess`）で返る。
 */
export class VerifyIdentificationError extends Error {
  constructor(
    readonly reason: 'unreadable' | 'requestFailed',
    readonly status?: number,
    message?: string,
  ) {
    super(message ?? reason)
    this.name = 'VerifyIdentificationError'
  }
}

/** アップロードした画像を判定する。 */
export const verifyIdentificationImage = async (arg: {
  hotelId: string
  idType: VerificationApiIdType
  objectKey: string
  /** 自動撮影で撮ったものか。手動シャッター・ファイル選択は `true` */
  isManual?: boolean
}): Promise<VerifyIdentificationSuccess> => {
  try {
    const response = await http<{ verify: boolean; nationality?: string }>({
      method: 'post',
      url: `${env('BASE_API_V3')}/identification/verify`,
      data: {
        hotelId: arg.hotelId,
        idType: arg.idType,
        imagePath: arg.objectKey,
        isManual: arg.isManual ?? true,
      },
    })
    return { verify: response.data.verify, nationality: response.data.nationality }
  } catch (error) {
    const axiosError = error as AxiosError<{ message?: string; errorMessage?: string }>
    const status = axiosError?.response?.status
    throw new VerifyIdentificationError(
      status === 400 ? 'unreadable' : 'requestFailed',
      status,
      axiosError?.response?.data?.message || axiosError?.response?.data?.errorMessage,
    )
  }
}

export type IdentificationSlot = {
  slotId: string
  idType: VerificationApiIdType
  staffRegisterLabel: string
  order: number
}

/**
 * 判定の失敗は5種類ある。取り違えると撮り直しに繋がらない。
 * - `convertFailed`：HEIC変換・再エンコード・縮小の失敗 → 別のファイルで撮り直し
 * - `uploadFailed`：S3アップロードの通信失敗 → 再試行
 * - `unreadable`：判定APIの400「ファイルを読み取れませんでした」→ アップロードのやり直し
 * - `verifyFailed`：判定APIの通信そのものの失敗 → 再試行
 * - `retakeRequired`：`200` + `verify: false`（再撮影必須の設定で判定NG）→ 撮り直し
 */
export type SlotFailureReason = 'convertFailed' | 'uploadFailed' | 'unreadable' | 'verifyFailed' | 'retakeRequired'

export const slotFailureReasonForVerifyError = (
  reason: 'unreadable' | 'requestFailed' | undefined,
): Extract<SlotFailureReason, 'unreadable' | 'verifyFailed'> => (reason === 'unreadable' ? 'unreadable' : 'verifyFailed')

/**
 * 本人確認を済みとするかどうかの判断。据置端末と同じ規則
 * （aipass_hotel/src/components/organisms/self-checkin/identity-verify/identity-verify-camera.tsx:172）
 */
export const isIdentificationVerified = (verify: boolean, requiredIdentifyVerification: 0 | 1): boolean =>
  verify || requiredIdentifyVerification === 0

/** 1枠ぶんの下準備・アップロード・判定・確定判断を1本にした thunk。 */
export const submitIdentificationImage = createAsyncThunk<
  UploadedIdentificationImage,
  {
    hotelId: string
    reservationUuid?: string
    slot: IdentificationSlot
    file: Blob
    fileName: string
    isManual: boolean
    requiredIdentifyVerification: 0 | 1
  },
  { rejectValue: { slotId: string; reason: SlotFailureReason } }
>('checkIn/identification/submit', async (arg, { rejectWithValue }) => {
  const slotId = arg.slot.slotId

  let prepared: PreparedImage
  try {
    prepared = await prepareImageForUpload(arg.file, arg.fileName)
  } catch {
    return rejectWithValue({ slotId, reason: 'convertFailed' })
  }

  let objectKey: string
  try {
    ;({ objectKey } = await uploadIdentificationImage({
      hotelId: arg.hotelId,
      reservationUuid: arg.reservationUuid,
      file: prepared.blob,
      fileName: prepared.fileName,
    }))
  } catch {
    return rejectWithValue({ slotId, reason: 'uploadFailed' })
  }

  let verifyResult: VerifyIdentificationSuccess
  try {
    verifyResult = await verifyIdentificationImage({
      hotelId: arg.hotelId,
      idType: arg.slot.idType,
      objectKey,
      isManual: arg.isManual,
    })
  } catch (error) {
    const reason = slotFailureReasonForVerifyError(error instanceof VerifyIdentificationError ? error.reason : undefined)
    return rejectWithValue({ slotId, reason })
  }

  if (!isIdentificationVerified(verifyResult.verify, arg.requiredIdentifyVerification)) {
    return rejectWithValue({ slotId, reason: 'retakeRequired' })
  }

  return {
    slotId,
    idType: arg.slot.idType,
    objectKey,
    previewUrl: URL.createObjectURL(prepared.blob),
    nationality: verifyResult.nationality,
    staffRegisterLabel: arg.slot.staffRegisterLabel,
    order: arg.slot.order,
  }
})

/**
 * 登録の対象になる在住地。
 *
 * **日本在住と海外在住は排他。** 送る値（書類種別 / `name`）が違い、
 * 既存のセルフチェックインも片方だけを登録している
 * （`aipass_hotel/src/hooks/use-self-check-in-state.ts:243,254`）。
 */
export type IdentificationResidence = 'japanese' | 'overseas'

/**
 * チェックインで送る国籍。本人確認で選んだ在住地から決める。
 *
 * 本人確認を行っていない・スキップしたとき（`residence` が `undefined`）は登録情報のまま。
 */
export const nationalityForResidence = (
  residence: IdentificationResidence | undefined,
  selectedNationality: string | undefined,
  registeredNationality: string,
): string => {
  if (residence === undefined) {
    return registeredNationality
  }
  return residence === 'overseas' ? selectedNationality || 'NJP' : 'JPN'
}

/** 実際には「日本在住向けの書類か」を表す（登録APIのキー名のため変えられない） */
export const isDriversLicenseForIdType = (idType: VerificationApiIdType): 0 | 1 => (idType === 'passport' ? 0 : 1)

/**
 * チェックイン確定後に画像を登録する。
 *
 * **`POST /smart-checkin` の後に呼ぶ。** `checkinId` はそのレスポンスで生成されるため、
 * 確定より前には登録できない。認証トークンは `src/apis/aipass.ts` が全リクエストに
 * 自動で付けるので、ここで足すものは無い。
 *
 * **失敗してもチェックイン自体は成立させる。** 呼び出し側は `.unwrap()` せず、
 * 結果を見ずに次へ進める（据置端末の
 * `aipass_hotel/src/hooks/use-self-check-in-state.ts:242,271` と同じ扱い）。
 *
 * **日本在住向けの書類と海外在住向けの書類を同時に送らない。**
 * `switchIdentificationResidence` が在住地を切り替えた時点で画像を捨てているうえで、
 * 送る直前にも選ばれている在住地で絞る。
 */
export const registerIdentificationImagesAfterCheckIn = createAsyncThunk<
  { registeredCount: number },
  { hotelId: string; checkinId: string },
  { state: RootState; rejectValue: { message?: string } }
>('checkIn/identification/register', async (arg, { getState, rejectWithValue }) => {
  const { images: uploadedImages, residence } = getState().checkIn.identification
  if (residence === undefined) {
    // 在住地が確定していない＝スキップした・そもそも登録していない。
    // ここでフィルタを外して全部送ると、日本在住と海外在住が万一同時に
    // 残っていたときに両方送ってしまうため、確定していなければ何も送らない
    return { registeredCount: 0 }
  }
  // 選ばれている在住地の書類だけ送る（海外在住は passport、日本在住はそれ以外）
  // 枠の並び順で送る（代表者 → 同行者）。オブジェクトのキー順に依存させない
  const images = Object.values(uploadedImages)
    .filter(image => (residence === 'overseas') === (image.idType === 'passport'))
    .sort((a, b) => a.order - b.order)
  if (!images.length) {
    // スキップした・そもそも登録していない。呼ばない
    return { registeredCount: 0 }
  }

  try {
    await http({
      method: 'post',
      url: `${env('BASE_API_V3')}/smart-checkin/identification-images`,
      data: {
        hotelId: arg.hotelId,
        identification: {
          checkinId: arg.checkinId,
          images: images.map(image => ({
            isDriversLicense: isDriversLicenseForIdType(image.idType),
            objectKey: image.objectKey,
            name: image.staffRegisterLabel, // 登録APIのキー名は `name`（アプリ内の名前とは揃えない）
          })),
        },
      },
    })
    return { registeredCount: images.length }
  } catch (error) {
    return rejectWithValue({ message: (error as AxiosError)?.message })
  }
})

type IdentificationState = {
  images: { [slotId: string]: UploadedIdentificationImage }
  /** キーがあれば送信中。値は requestId */
  submitting: { [slotId: string]: string }
  failure?: { slotId: string; reason: SlotFailureReason }
  residence?: IdentificationResidence
  selectedNationality?: string
  nationalitySource?: 'scan' | 'manual'
  /**
   * このチェックインで本人確認の手続きを終えたか。**登録した場合とスキップした場合の両方で立つ。**
   * loader（`src/loaders/identity-verify-loader.ts`）はこれが立っていれば本人確認を挟まない。
   * 立っていないと、本人確認を終えて確認画面へ進んだ瞬間にまた本人確認へ戻されループする。
   */
  hasFinishedIdentityVerify: boolean
}

const initialState: IdentificationState = {
  images: {},
  submitting: {},
  hasFinishedIdentityVerify: false,
}

/**
 * `requestId` が今の枠の進行と一致するか。撮り直しや枠の入れ替え（`removeIdentificationImage`）で
 * `submitting[slotId]` が変わっていれば、遅れて届いた古い結果は一致しない
 */
const isCurrentSubmit = (state: IdentificationState, slotId: string, requestId: string): boolean => state.submitting[slotId] === requestId

export const identificationSlice = createSlice({
  name: 'identification',
  initialState,
  reducers: {
    /** 1枠ぶんの画像を捨てる（撮り直し・同行者の人数を減らしたとき） */
    removeIdentificationImage: (state, action: PayloadAction<{ slotId: string }>) => {
      const slotId = action.payload.slotId
      const image = state.images[slotId]
      if (image) {
        URL.revokeObjectURL(image.previewUrl)
        delete state.images[slotId]
        // 画像が減れば必要な枚数を満たさなくなり得る。済みの印は落とし、判断をやり直させる
        state.hasFinishedIdentityVerify = false
      }
      delete state.submitting[slotId]
      if (state.failure?.slotId === slotId) {
        state.failure = undefined
      }
    },
    /** 全部捨てる（チェックイン完了後） */
    resetIdentification: state => {
      Object.values(state.images).forEach(image => URL.revokeObjectURL(image.previewUrl))
      state.images = {}
      state.submitting = {}
      state.failure = undefined
      state.residence = undefined
      state.selectedNationality = undefined
      state.nationalitySource = undefined
      state.hasFinishedIdentityVerify = false
    },
    /**
     * 在住地を選ぶ。
     *
     * **切り替えたら登録済みの画像を全部捨てる。** 日本在住と海外在住が
     * 同時に登録されないことを、ここで担保する。
     */
    switchIdentificationResidence: (state, action: PayloadAction<{ residence: IdentificationResidence }>) => {
      if (state.residence === action.payload.residence) {
        return
      }
      // 画面に入ったときの初回の確定は「切り替え」ではない。ここで捨てると、
      // スキップした直後にこの画面へ戻っただけで本人確認の印まで落ちる
      if (state.residence === undefined) {
        state.residence = action.payload.residence
        return
      }
      Object.values(state.images).forEach(image => URL.revokeObjectURL(image.previewUrl))
      state.images = {}
      state.submitting = {}
      state.failure = undefined
      state.residence = action.payload.residence
      state.selectedNationality = undefined
      state.nationalitySource = undefined
      state.hasFinishedIdentityVerify = false
    },
    /**
     * 海外在住の国籍セレクトの値。
     *
     * 判定で読み取れたときは `source: 'scan'`、ゲストが選び直したときは `source: 'manual'` で呼ぶ。
     * 空にする（`nationality: undefined`）と、`nationalitySource` も一緒に消える。
     */
    setSelectedNationality: (state, action: PayloadAction<{ nationality?: string; source: 'scan' | 'manual' }>) => {
      state.selectedNationality = action.payload.nationality
      state.nationalitySource = action.payload.nationality ? action.payload.source : undefined
    },
    dismissIdentificationFailure: state => {
      state.failure = undefined
    },
    /** 本人確認の手続きを終えたことにする（登録して「次へ」、またはスキップ。両方から呼ぶ） */
    finishIdentityVerify: state => {
      state.hasFinishedIdentityVerify = true
    },
    /** 終えたことにした後で前提が変わったとき（宿泊人数の変更など）に、もう一度通させる */
    requireIdentityVerifyAgain: state => {
      state.hasFinishedIdentityVerify = false
    },
  },
  extraReducers: builder => {
    builder.addCase(submitIdentificationImage.pending, (state, action) => {
      state.submitting[action.meta.arg.slot.slotId] = action.meta.requestId
    })
    builder.addCase(submitIdentificationImage.fulfilled, (state, action) => {
      const slotId = action.payload.slotId
      if (!isCurrentSubmit(state, slotId, action.meta.requestId)) {
        // 枠が入れ替わった後に届いた古い結果。確保していたプレビュー URL を解放して捨てる
        URL.revokeObjectURL(action.payload.previewUrl)
        return
      }
      const previous = state.images[slotId]
      if (previous) {
        URL.revokeObjectURL(previous.previewUrl)
      }
      state.images[slotId] = action.payload
      delete state.submitting[slotId]
    })
    builder.addCase(submitIdentificationImage.rejected, (state, action) => {
      const slotId = action.payload?.slotId ?? action.meta.arg.slot.slotId
      if (!isCurrentSubmit(state, slotId, action.meta.requestId)) {
        return
      }
      state.failure = { slotId, reason: action.payload?.reason ?? 'verifyFailed' }
      delete state.submitting[slotId]
    })
  },
})

export const {
  removeIdentificationImage,
  resetIdentification,
  switchIdentificationResidence,
  setSelectedNationality,
  dismissIdentificationFailure,
  finishIdentityVerify,
  requireIdentityVerifyAgain,
} = identificationSlice.actions
export const identificationReducer = identificationSlice.reducer
